import { format } from 'date-fns'

import { CHARACTER_WHITESPACE } from '../../../../../constants/common'
import { DATE_FULL_FORMAT } from '../../../../../constants/date'
import { toUkTimezoneDate } from '../../../../../utils/date'
import { toRentalPortalTimelineItems } from '../../../RentalPortalTimelineMarker'
import { getListingPublishingsTabUrl } from '../RentalPortalListingProfile.utils'

import {
  LISTING_READINESS_KEY,
  LISTING_SCHEDULE_REFUSAL_REASON_KEY,
  LISTING_SCHEDULE_REFUSAL_TEXT_KEY,
} from './RentalPortalListingProfileWidgetPublishing.constants'

function getListingReadinessDoorByKey({
  canEdit,
  canEditPhotos,
  canViewPublications,
}) {
  const blockDoor = {
    isAllowed: canEdit,
    notAllowedHintKey: 'RentalPortal.ListingEditNotAllowedTooltipKey',
  }

  return {
    [LISTING_READINESS_KEY.description]: blockDoor,
    [LISTING_READINESS_KEY.photo]: {
      isAllowed: canEditPhotos,
      notAllowedHintKey:
        'RentalPortal.ListingProfileReadinessFixNotAllowedHintKey',
    },
    [LISTING_READINESS_KEY.portal]: {
      isAllowed: canViewPublications,
      notAllowedHintKey:
        'RentalPortal.ListingProfileManagePublishingNotAllowedTooltipKey',
    },
    [LISTING_READINESS_KEY.rent]: blockDoor,
  }
}

function getListingScheduleRefusalReasonText({ checks, refusal, t }) {
  const reasonKey =
    LISTING_SCHEDULE_REFUSAL_REASON_KEY[refusal.action][refusal.reason]

  return reasonKey
    ? t(reasonKey, {
        missing: checks
          .filter(check => refusal.missing.includes(check.key))
          .map(check => t(check.label))
          .join(', '),
      })
    : null
}

function getListingReadinessRowState({ doorByKey, isDone, key }) {
  const door = doorByKey[key]

  if (isDone) {
    return { hintKey: null, isFixable: false }
  }

  if (!door) {
    return {
      hintKey: 'RentalPortal.ListingProfileReadinessFixUnavailableHintKey',
      isFixable: false,
    }
  }

  return door.isAllowed
    ? { hintKey: null, isFixable: true }
    : { hintKey: door.notAllowedHintKey, isFixable: false }
}

export function getListingReadinessSummary(checks) {
  const doneCount = checks.filter(check => check.is_done).length

  return {
    doneCount,
    isComplete: checks.length > 0 && doneCount === checks.length,
    total: checks.length,
  }
}

export function getListingReadinessRows({
  canEdit,
  canEditPhotos,
  canViewPublications,
  checks,
}) {
  const doorByKey = getListingReadinessDoorByKey({
    canEdit,
    canEditPhotos,
    canViewPublications,
  })

  return toRentalPortalTimelineItems(
    checks.map(check => {
      const isDone = Boolean(check.is_done)

      return {
        ...getListingReadinessRowState({ doorByKey, isDone, key: check.key }),
        isDone,
        key: check.key,
        labelKey: check.label,
      }
    })
  )
}

export function getIsListingReadinessCollapsedByDefault({
  isReadinessComplete,
  listingData,
}) {
  return (
    listingData?.publishing?.readiness_collapsed_default ??
    (Boolean(listingData?.has_been_published) && isReadinessComplete)
  )
}

export function getIsListingReadinessExpanded({
  isCollapsedByDefault,
  isReadinessComplete,
  listingId,
  manualChoice,
}) {
  return (
    !isReadinessComplete ||
    (manualChoice && manualChoice.listingId === listingId
      ? manualChoice.isExpanded
      : !isCollapsedByDefault)
  )
}

export function getListingReadinessCollapseTooltipTexts({
  isDisabled,
  tooltipKey,
}) {
  return isDisabled
    ? { closeTooltipText: tooltipKey, openTooltipText: tooltipKey }
    : {
        closeTooltipText: 'Common.WidgetHeaderExpandButtonCloseTooltipKey',
        openTooltipText: 'Common.WidgetHeaderExpandButtonOpenTooltipKey',
      }
}

export function getListingManagePublishingState(listingData) {
  const isDisabled = !listingData?.permissions?.can_view_publications

  return {
    isDisabled,
    tooltipKey: isDisabled
      ? 'RentalPortal.ListingProfileManagePublishingNotAllowedTooltipKey'
      : null,
    url: getListingPublishingsTabUrl(listingData?.id),
  }
}

export function getListingScheduleRefusalText({
  checks,
  refusal,
  t,
  timezone,
}) {
  return refusal && LISTING_SCHEDULE_REFUSAL_TEXT_KEY[refusal.action]
    ? [
        t(LISTING_SCHEDULE_REFUSAL_TEXT_KEY[refusal.action], {
          datetime: format(
            toUkTimezoneDate(refusal.scheduled_for, timezone),
            DATE_FULL_FORMAT
          ),
        }),
        getListingScheduleRefusalReasonText({ checks, refusal, t }),
      ]
        .filter(Boolean)
        .join(CHARACTER_WHITESPACE)
    : null
}
